import * as THREE from 'three';
import {
    Batch,
    Edge,
    OperationGroup,
    PreviewFormValues,
} from 'components/customer/Preview3D/types';
import {
    createPanelGeometry,
    getPantryDuplicateBoxTopZ,
    isPantryDuplicateBoxTop,
} from 'components/customer/Preview3D/helpers';
import {
    RoomPlannerProduct,
    RoomPlannerFormValues,
} from 'components/customer/RoomPlanner/types';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import {attachAnimations} from 'components/customer/Preview3DCommon/animations/handler';
import {
    ShakerDoorInfo,
    hasSplitDoors,
    buildsShakerPerOperationGroup,
    isDoorOperationGroup,
    isSplitDoorOperationGroup,
} from 'components/customer/Preview3DCommon/helpers/shakerDoorHelper';
import {isDoorOrDrawerFront} from 'components/customer/RoomPlanner/helpers/doorFronts';
import {roleOfOperationGroup} from 'components/customer/RoomPlanner/helpers/componentRoles';
import {applyOperationGroupTransform} from 'components/customer/Preview3DCommon/helpers/applyOperationGroupTransform';
import {getPanelThickness} from 'components/customer/Preview3DCommon/shapes/advancedShelf';
import addShakerDoorWithAnimation from 'components/customer/Preview3DCommon/hardware/CreateShakerDoor';
import {getRightLShapeBifoldLeftDoorXOffset} from 'components/customer/Preview3DCommon/values/door';
import {
    BevelFaceFlags,
    createBeveledPanelGeometry,
    isBeveledFramePanel,
} from 'components/customer/Preview3DCommon/hardware/useBeveledFrame';
import {
    loadCachedTexture,
    resolveTexture,
} from 'components/customer/Preview3D/helpers/Three';
import {isFloatingVanityTemplate} from 'components/customer/RoomPlanner/helpers/renderer/panelTypeHelpers';

const SUBSTRATE_TEXTURE_URL = '/templates/3D/textures/MDF.jpg';

const getEdgeValue = (
    panelValue: string | undefined,
    cabinetValue: string | undefined
): boolean => {
    if (typeof panelValue !== 'undefined') {
        return Boolean(parseInt(panelValue));
    }
    if (typeof cabinetValue !== 'undefined') {
        return Boolean(parseInt(cabinetValue));
    }
    return false;
};

export const createSceneXML = (
    batch: Batch,
    values: RoomPlannerProduct,
    variables: TemplateVariable,
    showTexture?: boolean,
    includeDrawerFaces?: boolean,
    showShelfLabel?: boolean,
    isWireframeMode?: boolean,
    thickness?: {
        exteriorThickness: number;
        carcaseThickness: number;
    },
    shakerDoorInfo?: ShakerDoorInfo,
    isKickerOrBulkhead?: boolean
) => {
    // Shared cached handle: one MDF download for the whole room rather than
    // two per cabinet.
    const substrateTextureTemp = loadCachedTexture(SUBSTRATE_TEXTURE_URL);
    const substrateTexture = resolveTexture(SUBSTRATE_TEXTURE_URL);
    const defaultMaterial = new THREE.MeshStandardMaterial({
        map: substrateTexture,
    });

    if (typeof batch === 'undefined' || batch == null) return;

    const cabinetGroup = new THREE.Group();
    const previewValues = {...values} as unknown as PreviewFormValues;

    const isFloatingVanity = isFloatingVanityTemplate(
        values.template_3d?.[0]?.attributes?.template
    );

    const edging: Edge | null =
        typeof previewValues.cabinet_edge_L1 !== 'undefined' ||
        typeof previewValues.panel_edge_top !== 'undefined'
            ? {
                  l1: getEdgeValue(
                      previewValues.panel_edge_left as string,
                      previewValues.cabinet_edge_L1 as string
                  ),
                  l2: getEdgeValue(
                      previewValues.panel_edge_right as string,
                      previewValues.cabinet_edge_L2 as string
                  ),
                  w1: getEdgeValue(
                      previewValues.panel_edge_top as string,
                      previewValues.cabinet_edge_W1 as string
                  ),
                  w2: getEdgeValue(
                      previewValues.panel_edge_bottom as string,
                      previewValues.cabinet_edge_W2 as string
                  ),
              }
            : null;
    // 13 - Panel Adjustable Hole
    if (values.cabinet_type == 13) {
        const prevEdging = {...edging};
        edging.l1 = prevEdging.l2;
        edging.l2 = prevEdging.l1;
        edging.w1 = prevEdging.w2;
        edging.w2 = prevEdging.w1;
    }

    // 85 - Kick Length
    if (
        values.cabinet_type == 85 ||
        values.cabinet_type == 229 ||
        values.cabinet_type == 230 ||
        values.type_name == 'KickLength'
    ) {
        const prevEdging = {...edging};
        edging.l1 = prevEdging.w1;
        edging.l2 = prevEdging.w2;
        edging.w1 = prevEdging.l1;
        edging.w2 = prevEdging.l2;
    }

    let counter = 0;

    const meshesToUpdate: THREE.Group<THREE.Object3DEventMap>[] = [];

    // 2-part pantry duplicate box-top height (one thickness under partition).
    const pantryCarcaseThickness =
        thickness?.carcaseThickness || values.cabinet_carc_thickness || 16.5;
    const pantryBoxTopZ = getPantryDuplicateBoxTopZ(
        variables?.shelfType === 'PANTRY' && !variables?.is2Part,
        previewValues.cabinet_partition_height,
        pantryCarcaseThickness
    );

    for (const job of Object.values(batch.Jobs)) {
        for (const manufacturing of Object.values(job.Manufacturing)) {
            const operationGroups = Object.values(
                manufacturing.OperationGroups
            );
            const isPairDoor = operationGroups.some(
                (g) => g.IsPairDoor || g.IsLeftBifoldExtension
            );
            const isBiFoldDoor = operationGroups.some(
                (g) =>
                    g.IsBifoldLeftDoor ||
                    g.IsLeftBifoldExtension ||
                    g.IsLeftBifoldExtension
            );

            const isTopHang = operationGroups.some((g) => g.IsTopHangDoor);
            const isSplitDoor = hasSplitDoors(operationGroups);

            const isRightDoor = operationGroups.some((g) => g.IsRightDoor);
            const isRightLShape = operationGroups.some((g) => g.IsRightLShape);

            for (const operationGroup of operationGroups) {
                if (!isValidPositionOperationGroup(operationGroup, true)) {
                    continue;
                }

                if (
                    !includeDrawerFaces &&
                    typeof includeDrawerFaces !== 'undefined' &&
                    operationGroup.ExteriorMaterial
                ) {
                    continue;
                }

                // Skip pantry duplicate box-top; keep the labelled partition.
                if (isPantryDuplicateBoxTop(operationGroup, pantryBoxTopZ)) {
                    continue;
                }

                const includeHardware = Boolean(
                    previewValues.cabinet_include_hardware
                );

                let isDoor = isDoorOperationGroup(operationGroup);

                let nonSupplyCarcase =
                    operationGroup.IsDrawerRunner &&
                    !Boolean(values.cabinet_include_hardware);

                if (
                    (variables.innerDrawerCount &&
                        !variables.isBinWithInnerDrawerProduct) ||
                    values?.isInnerDrawer
                ) {
                    if (operationGroup.IsDrawerFront) {
                        isDoor = false;
                        nonSupplyCarcase = !includeHardware;
                    }
                }

                if (!includeDrawerFaces && operationGroup.ExteriorMaterial) {
                    continue;
                }

                if (
                    operationGroup.IsDrawerFront ||
                    operationGroup.IsDoorPullout
                )
                    counter++;

                let mesh;

                if (
                    shakerDoorInfo?.isShakerDoor &&
                    isDoor &&
                    buildsShakerPerOperationGroup(operationGroup, {
                        isBiFoldDoor,
                        isTopHang,
                        isSplitDoor,
                    })
                ) {
                    mesh = addShakerDoorWithAnimation({
                        operationGroup,
                        variables,
                        values: previewValues,
                        exteriorThickness: thickness.exteriorThickness,
                        carcaseThickness: thickness.carcaseThickness,
                        showTexture,
                        isPairDoor,
                        isRightDoor,
                        isBiFoldDoor,
                        isRightLShape,
                        isSplitDoor: isSplitDoorOperationGroup(operationGroup),
                    });
                } else if (
                    isKickerOrBulkhead &&
                    isBeveledFramePanel(operationGroup)
                ) {
                    // Kicker frame / bulkhead panels get 45-degree beveled
                    // edges so adjacent panels join seamlessly.
                    const isExtMat =
                        Boolean(operationGroup.ExteriorMaterial) ||
                        Boolean(operationGroup.CarcaseMaterial)
                            ? operationGroup.ExteriorMaterial?.toString() ===
                              'true'
                            : false;
                    const bevelMaterialImage = isExtMat
                        ? ((values.cabinet_ext_image
                              ? values.cabinet_ext_image
                              : applyImagePath(values.ext_material?.image)) ??
                          null)
                        : ((values.cabinet_carc_image
                              ? values.cabinet_carc_image
                              : applyImagePath(values.carc_material?.image)) ??
                          null);
                    mesh = createBeveledPanelGeometry(
                        undefined,
                        operationGroup,
                        '#000',
                        showTexture,
                        bevelMaterialImage,
                        true,
                        isExtMat,
                        {
                            exteriorThickness:
                                thickness?.exteriorThickness || 16.5,
                            carcaseThickness:
                                thickness?.carcaseThickness || 16.5,
                        },
                        isWireframeMode,
                        operationGroups?.length ?? 0,
                        {
                            hasBackFace: Boolean(values.cabinet_edge_L1),
                            hasLeftFace: Boolean(values.cabinet_edge_W1),
                            hasRightFace: Boolean(values.cabinet_edge_W2),
                            hasFrontFace: Boolean(values.cabinet_edge_L2),
                        } satisfies BevelFaceFlags,
                        Boolean(values.hor_grain_ext),
                        Boolean(values.hor_grain_carc)
                    );
                } else {
                    const {panelThickness, centerOffset} = getPanelThickness(
                        values as unknown as PreviewFormValues,
                        Boolean(operationGroup?.ShelfLabel),
                        thickness?.carcaseThickness || 16.5
                    );

                    mesh = createPanelGeometry(
                        undefined,
                        operationGroup,
                        '#000',
                        showTexture,
                        values.cabinet_ext_image
                            ? values.cabinet_ext_image
                            : applyImagePath(values.ext_material?.image),
                        values.cabinet_carc_image
                            ? values.cabinet_carc_image
                            : applyImagePath(values.carc_material?.image),
                        values.cabinet_ext_edge_image
                            ? values.cabinet_ext_edge_image
                            : applyImagePath(values.ext_edge?.image),
                        values.cabinet_carc_edge_image
                            ? values.cabinet_carc_edge_image
                            : applyImagePath(values.carc_edge?.image),
                        true,
                        Boolean(operationGroup.ExteriorMaterial) ||
                            Boolean(operationGroup.CarcaseMaterial)
                            ? operationGroup.ExteriorMaterial?.toString() ===
                                  'true'
                            : null,
                        Boolean(values.hor_grain_ext),
                        Boolean(values.hor_grain_carc),
                        Boolean(operationGroup.NoEdge)
                            ? {
                                  l1: false,
                                  l2: false,
                                  w1: false,
                                  w2: false,
                              }
                            : edging, // edging

                        nonSupplyCarcase,
                        operationGroup.IsDrawerRunner ||
                            (operationGroup.IsDrawerFront &&
                                Boolean(variables?.innerDrawerCount) &&
                                !variables.isBinWithInnerDrawerProduct),
                        {
                            exteriorThickness:
                                thickness?.exteriorThickness || 16.5,
                            carcaseThickness: panelThickness,
                            centerOffset,
                        },
                        false,
                        false,
                        operationGroup?.ShelfLabel,
                        showShelfLabel,
                        defaultMaterial,
                        defaultMaterial,
                        true,
                        variables?.isLeftReturn,
                        variables?.isReturnProduct && !variables?.isLeftReturn,
                        operationGroups?.length,
                        variables?.isUpperProduct ||
                            variables?.isApplianceProduct ||
                            isFloatingVanity,
                        Boolean(operationGroup.NoEdging),
                        operationGroup.IsRightDoor ||
                            operationGroup.IsPairDoor ||
                            operationGroup.IsLeftDoor ||
                            operationGroup.IsCorFoldKitLeftDoor ||
                            operationGroup.IsCorFoldKitRightDoor ||
                            operationGroup.IsDoorPullout ||
                            operationGroup.IsTopHangDoor ||
                            operationGroup.IsBifoldLeftDoor ||
                            operationGroup.IsBifoldRightDoor ||
                            operationGroup.IsCornerLeftDoor ||
                            operationGroup.IsCornerRightDoor ||
                            operationGroup.IsDrawerFront ||
                            operationGroup.WithEdging ||
                            operationGroup.IsLeftApplianceMainDoor ||
                            operationGroup.IsLeftApplianceExtendedDoor ||
                            operationGroup.IsRightApplianceMainDoor ||
                            operationGroup.IsRightApplianceExtendedDoor ||
                            (operationGroup.ExteriorMaterial &&
                                variables?.isReturnProduct),
                        Boolean(operationGroup.FlipSubstrate),
                        Boolean(operationGroup.IsFrontRangehoodVent),
                        Boolean(operationGroup.WithTopEdging),
                        Boolean(operationGroup.WithFrontEdging),
                        Boolean(operationGroup.DynamicExteriorEdging),
                        isWireframeMode,
                        Boolean(operationGroup.WithBackEdging)
                    );

                    if (operationGroup.IsBifoldLeftDoor) {
                        mesh.position.x += getRightLShapeBifoldLeftDoorXOffset(
                            Boolean(operationGroup.IsRightLShape),
                            Boolean(operationGroup.IsLeftBifoldExtension),
                            thickness?.exteriorThickness
                        );
                    }

                    if (isDoor && shakerDoorInfo?.isShakerDoor) {
                        // creating the mesh for a shaker door but hiding it so we can have valid position for this cabinetGroup
                        // invalid position for an empty group affects other cabinets from spawning as spawn position is calculated based on it
                        mesh.visible = false;
                    }
                }

                // Shaker replacements stay hidden placeholders. The visible
                // shaker group is tagged when it is added later.
                const isShakerPlaceholder =
                    Boolean(shakerDoorInfo?.isShakerDoor) &&
                    isDoor &&
                    !(isBiFoldDoor || isTopHang);
                if (
                    isDoorOrDrawerFront(operationGroup) &&
                    !isShakerPlaceholder
                ) {
                    mesh.userData.isDoorOrDrawerFront = true;
                }
                // Front, carcase, internal or hardware, for what blocks and
                // lines up while dragging (componentRoles.ts).
                mesh.userData.componentRole =
                    roleOfOperationGroup(operationGroup).role;

                meshesToUpdate.push(mesh);

                // Canonical door-positioning pipeline shared with Preview3D.
                const meshPos = applyOperationGroupTransform({
                    mesh,
                    operationGroup,
                    variables,
                    values: previewValues,
                    thickness: {
                        exteriorThickness:
                            thickness?.exteriorThickness ||
                            values.cabinet_ext_thickness ||
                            16.5,
                        carcaseThickness:
                            thickness?.carcaseThickness ||
                            values.cabinet_carc_thickness ||
                            16.5,
                    },
                    shakerDoorInfo,
                    templateName: values.template_3d[0].attributes.template,
                });

                attachAnimations(
                    mesh,
                    operationGroup,
                    values as unknown as RoomPlannerFormValues,
                    variables,
                    meshPos,
                    counter
                );

                cabinetGroup.add(mesh);
            }
        }
    }

    const box = new THREE.Box3().setFromObject(cabinetGroup);
    const cabinetSize = box.getSize(new THREE.Vector3());

    cabinetGroup.position.set(
        -box.min.x - cabinetSize.x / 2,
        -box.min.y - cabinetSize.y / 2,
        0
    );

    substrateTextureTemp
        .then((substrateTexture) => {
            const textureMaterial = new THREE.MeshStandardMaterial({
                map: substrateTexture,
            });

            // Update all stored meshes with the loaded texture
            meshesToUpdate.forEach((mesh) => {
                mesh.traverse((child) => {
                    // First check if it's a Mesh using instanceof
                    if (child instanceof THREE.Mesh) {
                        // Now TypeScript knows child is a Mesh and has material property
                        if (child.material instanceof THREE.Material) {
                            // For single materials
                            if (child.material === defaultMaterial) {
                                child.material = textureMaterial;
                            }
                        } else if (Array.isArray(child.material)) {
                            // For objects with material arrays
                            child.material = child.material.map(
                                (mat: THREE.MeshStandardMaterial) =>
                                    mat === defaultMaterial
                                        ? textureMaterial
                                        : mat
                            );
                        }
                    }
                });
            });
        })
        .catch((error) => {
            // eslint-disable-next-line no-console -- this is a debug message
            console.warn('Texture failed to load:', error);
        });

    return cabinetGroup;
};

export const isValidPositionOperationGroup = (
    operationGroup: OperationGroup,
    debug = false
): boolean => {
    let valid = true;

    if (operationGroup && operationGroup.Operations) {
        operationGroup.Operations.forEach((operation, operationIndex) => {
            if (operation.Insert) {
                const {x, y, z} = operation.Insert;
                if (isNaN(x) || isNaN(y) || isNaN(z)) {
                    if (debug) {
                        // eslint-disable-next-line no-console -- this is a debug message
                        console.warn(
                            `Invalid Insert position detected in operation ${operationIndex}:`,
                            {x, y, z}
                        );
                    }
                    valid = false;
                }
            }

            if (operation.Segments) {
                operation.Segments.forEach((segment, segmentIndex) => {
                    if (segment.StartNormal) {
                        const {x, y, z} = segment.StartNormal;
                        if (isNaN(x) || isNaN(y) || isNaN(z)) {
                            if (debug) {
                                // eslint-disable-next-line no-console -- this is a debug message
                                console.warn(
                                    `Invalid StartNormal detected in segment ${segmentIndex} of operation ${operationIndex}:`,
                                    {x, y, z}
                                );
                            }
                            valid = false;
                        }
                    }
                });
            }
        });
    }

    return valid;
};

export const applyImagePath = (image: string | undefined) => {
    if (!image) return image;
    if (image.startsWith('/uploads')) {
        return image;
    }
    return `/uploads/gocabinet_materials/${image}`;
};
